import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  IconButton,
  Stack,
  Typography,
  Divider,
  Tooltip,
  Paper,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";

const formatDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("es-AR");
  } catch {
    return String(v);
  }
};

const safe = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
};

function InfoRow({ label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ py: 0.5 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 120 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, wordBreak: "break-word" }}
      >
        {safe(value)}
      </Typography>
    </Stack>
  );
}

function Section({ title, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

export function LiveCommentDetailDialog({ open, payload, onClose }) {
  const comment = payload?.comment;
  const author = comment?.author;
  const reporter = payload?.reporter;
  const live = comment?.live;

  const authorName = author
    ? `${author?.name ?? ""} ${author?.lastname ?? ""}`.trim()
    : "—";

  const reporterName = reporter
    ? `${reporter?.name ?? ""} ${reporter?.lastname ?? ""}`.trim()
    : "—";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,.25)",
        },
      }}
    >
      <DialogTitle sx={{ px: 3, py: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: "action.hover",
              }}
            >
              <ArticleRoundedIcon />
            </Box>

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                Detalle del reporte
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mt: 0.5, flexWrap: "wrap" }}
              >
                {payload?.reason ? (
                  <Chip
                    size="small"
                    label={payload.reason}
                    variant="outlined"
                    sx={{ fontWeight: 700 }}
                  />
                ) : null}

                {payload?.createdAt ? (
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(payload.createdAt)}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          </Stack>

          <Tooltip title="Cerrar">
            <IconButton onClick={onClose} size="small">
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent dividers sx={{ bgcolor: "background.default" }}>
        {!comment ? (
          <Typography color="text.secondary">No hay detalle para mostrar.</Typography>
        ) : (
          <Stack spacing={2}>
            <Section title="Reporte">
              <InfoRow label="ID del reporte" value={payload?.id} />
              <InfoRow label="Razón" value={payload?.reason} />
              <InfoRow label="Fecha del reporte" value={formatDate(payload?.createdAt)} />
            </Section>

            <Section title="Quien reportó">
              <InfoRow label="Nombre" value={reporterName} />
              <InfoRow label="Email" value={reporter?.email} />
              <InfoRow label="ID" value={reporter?.id} />
            </Section>

            <Section title="Comentario reportado">
              <InfoRow label="ID del comentario" value={comment?.id} />
              <InfoRow label="Fecha" value={formatDate(comment?.createdAt)} />

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Texto
              </Typography>

              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {safe(comment?.comment)}
                </Typography>
              </Paper>
            </Section>

            <Section title="Autor del comentario">
              <InfoRow label="Nombre" value={authorName} />
              <InfoRow label="Email" value={author?.email} />
              <InfoRow label="ID" value={author?.id} />
            </Section>

            <Section title="Live">
              <InfoRow label="ID del live" value={comment?.livesId} />
              <InfoRow label="Título" value={live?.title ?? `Live #${comment?.livesId}`} />
            </Section>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ borderRadius: 2 }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
